import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { NoData } from "@";
import Pagination from "../dataTable/Pagination";
import TableSkeleton from "./TableSkeleton";

export default function DataTable({
  data = [],
  columns = [],
  isLoading = false,
  pagination,
  onPageChange,
  onPageSizeChange,
  rowsPerPageOptions = [5, 10, 15, 25],
  className,
  noTableHead = false,
  isSmall = false,
  selectable = false,
  onSelectedChange,
  selectionResetKey,
  localPg = false,
  defaultPageSize = 10,
}) {
  const tableContainerRef = useRef(null);
  const [rowSelection, setRowSelection] = useState({});

  // Client-side pagination state — only consulted when `localPg` is on.
  // When `localPg` is off, the parent owns `pagination` like before.
  const [localPage, setLocalPage] = useState(1);
  const [localSize, setLocalSize] = useState(defaultPageSize);

  // Reset to page 1 whenever the underlying data length changes — otherwise
  // a search-filtered list could leave the user on page 5 of nothing.
  useEffect(() => {
    if (localPg) setLocalPage(1);
  }, [data?.length, localPg]);

  // One pagination object the rest of the table reads from. Either the
  // server-driven prop, or the locally-managed state above.
  const effectivePagination = useMemo(() => {
    if (localPg) {
      return { page: localPage, size: localSize, rows: data?.length || 0 };
    }
    return pagination;
  }, [localPg, localPage, localSize, data?.length, pagination]);

  // When local, slice the data ourselves before TanStack ever sees it.
  const effectiveData = useMemo(() => {
    if (!localPg) return data;
    const start = (localPage - 1) * localSize;
    return data.slice(start, start + localSize);
  }, [localPg, data, localPage, localSize]);

  const effectiveOnPageChange = localPg ? setLocalPage : onPageChange;
  const effectiveOnPageSizeChange = localPg
    ? (s) => {
        setLocalSize(s);
        setLocalPage(1);
      }
    : onPageSizeChange;

  // Selection column with refined checkbox styling
  const selectionColumn = useMemo(
    () => ({
      id: "select",
      header: ({ table }) => (
        <label className="inline-flex cursor-pointer items-center">
          <input
            type="checkbox"
            className="h-6 w-6 cursor-pointer rounded-md border-gray-300 text-purple-700 transition-all duration-150 focus:ring-2 focus:ring-purple-300 focus:ring-offset-1 dark:border-gray-600 dark:focus:ring-purple-700 dark:focus:ring-offset-gray-800 dark:bg-transparent "
            checked={table.getIsAllPageRowsSelected()}
            onChange={table.getToggleAllPageRowsSelectedHandler()}
          />
        </label>
      ),
      cell: ({ row }) => (
        <>
          {row.original.id !== "customRow" && (
            <label className="inline-flex cursor-pointer items-center">
              <input
                type="checkbox"
                className="h-6 w-6 cursor-pointer rounded-md border-gray-300 text-purple-700 transition-all duration-150 focus:ring-2 focus:ring-purple-300 focus:ring-offset-1 dark:border-gray-600 dark:focus:ring-purple-700 dark:focus:ring-offset-gray-800 dark:bg-transparent "
                checked={row.getIsSelected()}
                onChange={row.getToggleSelectedHandler()}
              />
            </label>
          )}
        </>
      ),
      size: 40,
    }),
    [],
  );

  const allColumns = useMemo(() => {
    if (selectable) {
      return [selectionColumn, ...columns];
    }
    return columns;
  }, [selectable, selectionColumn, columns]);

  const table = useReactTable({
    data: effectiveData,
    columns: allColumns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    rowCount: effectivePagination?.rows,
    defaultColumn: {
      size: 130,
    },
    enableRowSelection: selectable,
    onRowSelectionChange: setRowSelection,
    state: {
      rowSelection,
    },
  });

  useEffect(() => {
    if (selectable && onSelectedChange) {
      const selectedIndices = Object.keys(rowSelection).filter(
        (key) => rowSelection[key],
      );
      // Selection indices are relative to whatever data TanStack saw —
      // i.e. `effectiveData`, which is `data` for server pagination and
      // the current page slice for `localPg`.
      const selectedRows = selectedIndices
        .map((index) => effectiveData[parseInt(index)])
        .filter(Boolean);
      onSelectedChange({ selectedRows });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowSelection, selectable]);

  useEffect(() => {
    setRowSelection({});
    heightCache.current.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.length]);

  useEffect(() => {
    if (selectionResetKey !== undefined) {
      setRowSelection({});
    }
  }, [selectionResetKey]);

  const { rows } = table.getRowModel();

  const heightCache = useRef(new Map());
  const measureElement = useCallback((element) => {
    if (!element) return undefined;
    const height = Math.round(element.getBoundingClientRect().height);
    const index = element.dataset?.index;
    if (index != null) {
      const cached = heightCache.current.get(index);
      if (cached != null && Math.abs(cached - height) <= 1) {
        return cached;
      }
      heightCache.current.set(index, height);
    }
    return height;
  }, []);

  const shouldMeasure =
    typeof window !== "undefined" &&
    navigator.userAgent.indexOf("Firefox") === -1;

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => 52,
    measureElement: shouldMeasure ? measureElement : undefined,
    overscan: 10,
  });

  const totalPages = effectivePagination
    ? Math.ceil(effectivePagination.rows / effectivePagination.size)
    : 0;

  const containerClass =
    className ||
    "relative my-5 md:my-7 rounded-2xl border border-gray-100 dark:border-gray-700";
  const isVirtualized = rows.length > 24;

  // Refined header / body / sticky styles
  const headerCellBase =
    "px-4 py-3.5 text-start md:text-sm text-xs text-gray-600 dark:text-gray-400";
  const stickyHeaderClass =
    "sticky right-0 z-[3] bg-gradient-to-l from-purple-50/30 via-gray-50/80 to-gray-50/80 dark:from-purple-950/30 dark:via-gray-900/80 dark:to-gray-900/80";
  const headerRowClass = "border-y border-gray-100 bg-[#F8F8F8] dark:bg-gray-700  ";
  const stickyCellBase = "sticky right-0 z-[2] bg-white dark:bg-gray-800";
  const cellBase =
    "relative px-4 py-3 text-start text-sm text-gray-700 dark:text-gray-300";
  const rowBase = "group/row relative transition-colors duration-150";

  const virtualRows = rowVirtualizer.getVirtualItems();
  const paddingTop = virtualRows.length > 0 ? virtualRows[0]?.start || 0 : 0;
  const paddingBottom =
    virtualRows.length > 0
      ? rowVirtualizer.getTotalSize() -
        (virtualRows[virtualRows.length - 1]?.end || 0)
      : 0;

  useEffect(() => {
    if (tableContainerRef.current) {
      setRowSelection({});
      tableContainerRef.current.scrollTop = 0;
    }
  }, [effectivePagination?.page]);

  if (isLoading) {
    return (
      <div className={containerClass}>
        <TableSkeleton isSmall={isSmall} />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className={containerClass}>
        <div className="py-8">
          <NoData />
        </div>
        {effectivePagination && (
          <Pagination
            totalRows={0}
            currentPage={effectivePagination.page}
            rowsPerPage={effectivePagination.size}
            totalPages={0}
            onPageChange={effectiveOnPageChange}
            onRowsPerPageChange={effectiveOnPageSizeChange}
            rowsPerPageOptions={rowsPerPageOptions}
          />
        )}
      </div>
    );
  }

  // Render a single row's cells (DRY between virtualized + non-virtualized)
  const renderRow = (row, key, extraProps = {}) => {
    const isSelected = row.getIsSelected();
    return (
      <tr
        key={key}
        {...extraProps}
        className={`${rowBase} ${
          isSelected
            ? "bg-purple-50/40 hover:bg-purple-50/60 dark:bg-purple-950/30 dark:hover:bg-purple-950/40"
            : "bg-white hover:bg-gray-50/70 dark:bg-gray-800 dark:hover:bg-gray-700/40"
        }`}
      >
        {row.getVisibleCells().map((cell, cellIdx) => {
          const isSelectCell = cell.column.id === "select";
          return (
            <td
              key={cell.id}
              className={`${cellBase} ${
                isSelectCell
                  ? `${stickyCellBase} ${
                      isSelected
                        ? "!bg-purple-50/60 dark:!bg-purple-950/40"
                        : "group-hover/row:bg-gray-50/70 dark:group-hover/row:bg-gray-700/40"
                    }`
                  : ""
              }`}
            >
              {/* Start-side accent stripe for selected rows (only on first cell) */}
              {isSelected && cellIdx === 0 && (
                <span
                  aria-hidden
                  className="pointer-events-none absolute start-0 top-1/2 h-7 w-[3px] -translate-y-1/2 rounded-e-full bg-gradient-to-b from-purple-700 to-purple-950 dark:from-purple-400 dark:to-purple-600"
                />
              )}
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </td>
          );
        })}
      </tr>
    );
  };

  return (
    <div className={containerClass}>
      <div
        ref={tableContainerRef}
        style={{
          overflow: "auto",
          position: "relative",
          height: isVirtualized ? "65vh" : "auto",
        }}
        className="rounded-2xl"
      >
        {isVirtualized ? (
          <table className="datatable w-full min-w-max border-separate border-spacing-0">
            {!noTableHead && (
              <thead className={`sticky top-0 z-[9] ${headerRowClass}`}>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className={`${headerCellBase} ${
                          header.column.id === "select" ? stickyHeaderClass : ""
                        }`}
                        style={
                          header.column.columnDef.size
                            ? { width: header.column.columnDef.size }
                            : undefined
                        }
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
            )}

            <tbody className="virtualized-tbody">
              {paddingTop > 0 && (
                <tr>
                  <td
                    className="relative"
                    style={{ height: `${paddingTop}px` }}
                  />
                </tr>
              )}
              {virtualRows.map((virtualRow) => {
                const row = rows[virtualRow.index];
                return renderRow(row, row.id, {
                  "data-index": virtualRow.index,
                  ref: rowVirtualizer.measureElement,
                });
              })}
              {paddingBottom > 0 && (
                <tr>
                  <td
                    className="relative"
                    style={{ height: `${paddingBottom}px` }}
                  />
                </tr>
              )}
            </tbody>
          </table>
        ) : (
          <table className="w-full min-w-max border-separate border-spacing-0">
            {!noTableHead && (
              <thead className={`sticky top-0 z-[9] ${headerRowClass}`}>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className={`${headerCellBase} ${
                          header.column.id === "select" ? stickyHeaderClass : ""
                        }`}
                        style={
                          header.column.columnDef.size
                            ? { width: header.column.columnDef.size }
                            : undefined
                        }
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
            )}

            <tbody>
              {rows.map((row, idx) => {
                const isSelected = row.getIsSelected();
                const isLast = idx === rows.length - 1;
                return (
                  <tr
                    key={row.id}
                    className={`${rowBase} ${
                      !isLast
                        ? "border-b border-gray-100 dark:border-gray-700/50"
                        : ""
                    } ${
                      isSelected
                        ? "bg-purple-50/40 hover:bg-purple-50/60 dark:bg-purple-950/30 dark:hover:bg-purple-950/40"
                        : "bg-white hover:bg-gray-50/70 dark:bg-gray-800 dark:hover:bg-gray-700/40"
                    }`}
                  >
                    {row.getVisibleCells().map((cell, cellIdx) => {
                      const isSelectCell = cell.column.id === "select";
                      return (
                        <td
                          key={cell.id}
                          className={`${cellBase} ${
                            !isLast
                              ? "border-b border-gray-100 dark:border-gray-700/50"
                              : ""
                          } ${
                            isSelectCell
                              ? `${stickyCellBase} ${
                                  isSelected
                                    ? "!bg-purple-50/60 dark:!bg-purple-950/40"
                                    : "group-hover/row:bg-gray-50/70 dark:group-hover/row:bg-gray-700/40"
                                }`
                              : ""
                          }`}
                        >
                          {isSelected && cellIdx === 0 && (
                            <span
                              aria-hidden
                              className="pointer-events-none absolute start-0 top-1/2 h-7 w-[3px] -translate-y-1/2 rounded-e-full bg-gradient-to-b from-purple-700 to-purple-950 dark:from-purple-400 dark:to-purple-600"
                            />
                          )}
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {effectivePagination && (
        <Pagination
          totalRows={effectivePagination.rows}
          currentPage={effectivePagination.page}
          rowsPerPage={effectivePagination.size}
          totalPages={totalPages}
          onPageChange={effectiveOnPageChange}
          onRowsPerPageChange={effectiveOnPageSizeChange}
          rowsPerPageOptions={rowsPerPageOptions}
        />
      )}
    </div>
  );
}
